import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/tarieven", label: "Tarieven", testid: "nav-link-tarieven" },
  { to: "/zakelijk", label: "Zakelijk", testid: "nav-link-zakelijk" },
  { to: "/partners", label: "Partners", testid: "nav-link-partners" },
  { to: "/over-ons", label: "Over ons", testid: "nav-link-over-ons" },
  { to: "/faq", label: "FAQ", testid: "nav-link-faq" },
  { to: "/kennisbank", label: "Kennisbank", testid: "nav-link-kennisbank" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

const FOOTER_TARIEVEN = [
  "Appartement",
  "Tussenwoning",
  "Hoekwoning",
  "2-onder-1-kapwoning",
  "Vrijstaande woning",
];

export default function Layout() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    if (location.hash) {
      const t = setTimeout(() => {
        document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
    return undefined;
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      <div className="grain-overlay" aria-hidden="true" />

      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 border-b transition-[background-color,box-shadow] duration-300 backdrop-blur-xl ${
          scrolled ? "bg-white/80 border-black/5 shadow-sm" : "bg-white/60 border-transparent"
        }`}
      >
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="flex items-center justify-between py-4">
            <Link to="/" data-testid="logo-link" aria-label="Directelabels - naar home">
              <Logo />
            </Link>

            <nav className="hidden items-center gap-6 lg:flex xl:gap-8" aria-label="Hoofdnavigatie">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `text-sm font-semibold transition-colors duration-200 ${
                      isActive ? "text-primary" : "text-ink/70 hover:text-ink"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <Link
                to="/aanvragen"
                data-testid="header-cta-aanvragen"
                className="hidden items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-energy-light sm:inline-flex"
              >
                Aanvragen
                <ArrowUpRight size={16} />
              </Link>
              <button
                type="button"
                data-testid="mobile-menu-toggle"
                aria-label={open ? "Menu sluiten" : "Menu openen"}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-ink lg:hidden"
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {open && (
          <nav data-testid="mobile-menu" className="border-t border-black/5 bg-white/95 px-5 pb-6 pt-2 backdrop-blur-xl lg:hidden" aria-label="Mobiele navigatie">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-${item.testid}`}
                className={({ isActive }) =>
                  `block border-b border-black/5 py-4 font-display text-xl font-medium ${
                    isActive ? "text-primary" : "text-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/aanvragen"
              data-testid="mobile-cta-aanvragen"
              className="mt-5 flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 font-bold text-white"
            >
              Energielabel aanvragen
              <ArrowUpRight size={18} />
            </Link>
          </nav>
        )}
      </header>

      <main className="pt-[76px]">
        <Outlet />
      </main>

      <footer data-testid="site-footer" className="bg-energy-deep text-white">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-5">
              <Link to="/" data-testid="footer-logo-link" aria-label="Directelabels - naar home">
                <Logo dark />
              </Link>
              <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/60">
                Professionele EP-W woningopnames in Rotterdam en omgeving. Voor particulieren, makelaars, verhuurders
                en vastgoedpartijen — met registratie in EP-online.
              </p>
              <Link
                to="/aanvragen"
                data-testid="footer-cta-aanvragen"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-energy-deep transition-colors duration-200 hover:bg-energy-pale"
              >
                Energielabel aanvragen
                <ArrowUpRight size={16} />
              </Link>
            </div>

            <div className="md:col-span-2">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">Pagina's</h3>
              <ul className="mt-5 space-y-3 text-sm">
                {NAV.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} data-testid={`footer-${item.testid}`} className="text-white/70 transition-colors duration-200 hover:text-white">
                      {item.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/partners" data-testid="footer-nav-partners" className="text-white/70 transition-colors duration-200 hover:text-white">
                    Partners
                  </Link>
                </li>
                <li>
                  <Link to="/aanvragen" data-testid="footer-nav-aanvragen" className="text-white/70 transition-colors duration-200 hover:text-white">
                    Aanvragen
                  </Link>
                </li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">Tarieven</h3>
              <ul className="mt-5 space-y-3 text-sm">
                {FOOTER_TARIEVEN.map((s) => (
                  <li key={s}>
                    <Link to="/tarieven" data-testid={`footer-tarief-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-white/70 transition-colors duration-200 hover:text-white">
                      {s}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-3">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">Juridisch</h3>
              <ul className="mt-5 space-y-3 text-sm">
                <li>
                  <Link to="/privacyverklaring" data-testid="footer-link-privacy" className="text-white/70 transition-colors duration-200 hover:text-white">
                    Privacyverklaring
                  </Link>
                </li>
                <li>
                  <Link to="/algemene-voorwaarden" data-testid="footer-link-voorwaarden" className="text-white/70 transition-colors duration-200 hover:text-white">
                    Algemene voorwaarden
                  </Link>
                </li>
                <li>
                  <Link to="/cookies" data-testid="footer-link-cookies" className="text-white/70 transition-colors duration-200 hover:text-white">
                    Cookieverklaring
                  </Link>
                </li>
              </ul>
              <p className="mt-8 text-sm leading-relaxed text-white/50">
                Vragen? Neem contact op via het{" "}
                <Link to="/contact" data-testid="footer-link-contactform" className="font-semibold text-white underline decoration-primary decoration-2 underline-offset-4">
                  contactformulier
                </Link>
                . Wij reageren op werkdagen binnen 24 uur.
              </p>
              <div data-testid="footer-bedrijfsgegevens" className="mt-6 rounded-xl border border-dashed border-white/20 p-4 text-xs leading-relaxed text-white/50">
                <p className="font-bold uppercase tracking-[0.18em] text-white/40">Bedrijfsgegevens</p>
                <p className="mt-2">
                  E-mailadres:{" "}
                  <a href="mailto:info@directelabels.nl" data-testid="footer-mail-link" className="font-semibold text-white/70 underline decoration-white/30 underline-offset-2 hover:text-white">
                    info@directelabels.nl
                  </a>
                </p>
                <p className="mt-2">
                  Telefoon:{" "}
                  <a href="tel:+31657371945" data-testid="footer-phone-link" className="font-semibold text-white/70 underline decoration-white/30 underline-offset-2 hover:text-white">
                    06 - 5737 1945
                  </a>
                </p>
                <p className="mt-2">KvK-nummer volgt binnenkort.</p>
                <p className="mt-2 font-semibold text-white/60">Werkgebied: Rotterdam en omgeving</p>
              </div>
            </div>
          </div>

          <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/40 md:flex-row md:items-center">
            <p>© {new Date().getFullYear()} Directelabels. Alle rechten voorbehouden.</p>
            <p>Energielabels worden geregistreerd in de landelijke database EP-online.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
